import { msalInstance, msalReady } from '../auth/msalConfig';
import { loginRequest, tokenRequest } from '../authConfig';
import config from '../config';
import { isMobileDevice } from './deviceDetect';

/**
 * Get the current user ID from MSAL
 * @returns {string} The user ID or null if not logged in
 */
export const getUserId = () => {
  const account = msalInstance.getActiveAccount();

  // If no active account but we have accounts, set the first one as active
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      return accounts[0].username;
    }
  }

  return account?.username || null;
};

/**
 * Get the API base URL from the centralized config
 * @returns {string} The API base URL
 */
export const getApiBaseUrl = () => {
  return config.api.baseUrl;
};

/**
 * Normalize API path to ensure it starts with /api/
 * Handles both /profile and /api/profile gracefully
 * @param {string} path - The API path
 * @returns {string} Normalized path with /api/ prefix
 */
const normalizePath = (path) => {
  if (path.startsWith('/api/')) return path;
  if (path.startsWith('/')) return `/api${path}`;
  return `/api/${path}`;
};

/**
 * Login the user and set the active account
 * @returns {Promise<Object>} The login result
 */
export const login = async () => {
  try {
    const request = { ...loginRequest, prompt: 'select_account' };
    if (isMobileDevice()) {
      // Redirect flow: current page unloads; result is picked up on return
      // by handleRedirectPromise in index.js.
      await msalInstance.loginRedirect(request);
      return null;
    }
    const result = await msalInstance.loginPopup(request);
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    }
    return result;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Acquire an access token for the backend API.
 * Single source of truth — all API calls should use this function.
 * @returns {Promise<string|null>} The access token, or null if unavailable
 */
export const getAuthToken = async () => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    console.warn('No active account found');
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account,
    });
    return response.accessToken || null;
  } catch (error) {
    if (error.name === 'InteractionRequiredAuthError') {
      console.warn('Token requires user interaction — falling back to interactive acquisition');
      try {
        if (isMobileDevice()) {
          // Redirect flow: unloads the page. Token is available after return.
          await msalInstance.acquireTokenRedirect({ ...tokenRequest, account });
          return null;
        }
        const popupResponse = await msalInstance.acquireTokenPopup({ ...tokenRequest, account });
        return popupResponse?.accessToken || null;
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        return null;
      }
    }
    console.error('Token acquisition failed:', error);
    return null;
  }
};

/**
 * Enhanced fetch function that adds authentication token
 * @param {string} path - The API path (without base URL)
 * @param {Object} options - Fetch options. Extra non-fetch option:
 *   `directBackend: true` routes the request to the App Service origin
 *   instead of the relative SWA path — only for long-running calls that
 *   would be killed by the SWA gateway's ~45s proxy timeout. Identical to
 *   the normal path in development.
 *   `redirectOnGate: false` keeps a 402 from hard-navigating the page to
 *   /abonnement. For background calls (e.g. the profile→memory migration
 *   in utils/profileMigration.js) that would otherwise bounce a user off a
 *   gate-exempt page like /account or /admin. The typed error is still
 *   thrown either way — only the navigation is suppressed.
 * @returns {Promise} - Fetch promise
 */

// Pages a user without an active subscription is meant to reach. A 402 from
// a call made while on one of these must not navigate away — the page
// itself is allowed, only the gated call failed. Segment-aware: matches
// /admin and /admin/x, not /administrasjon.
const GATE_EXEMPT_PATH = /^\/(abonnement|admin)(\/|$)/;

/**
 * Thrown by authFetch on HTTP 429 — a per-user rate limit in the backend
 * (coord-backend/src/middlewares/rateLimit.js). `status` stays 429 so callers
 * that branch on err.status keep working. Never retried automatically: the
 * user decides when to try again (copy: utils/rateLimitMessage.js).
 *   retryAfterSeconds: from the Retry-After header, else the body; null if neither
 *   limiter: the backend limiter name from the body (e.g. 'conversations/create'), or null
 */
export class RateLimitError extends Error {
  constructor({ retryAfterSeconds = null, limiter = null, message = 'rate_limited' } = {}) {
    super(message);
    this.name = 'RateLimitError';
    this.status = 429;
    this.retryAfterSeconds = retryAfterSeconds;
    this.limiter = limiter;
  }
}

const positiveSeconds = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : null;
};

// The header is the primary source; the body covers responses where it is
// not readable (e.g. a cross-origin call that does not expose Retry-After).
const rateLimitErrorFrom = async (response) => {
  let text = '';
  let body = null;
  try {
    text = await response.text();
    body = JSON.parse(text);
  } catch (e) {
    // Unreadable or non-JSON body — the header (if any) still applies.
  }
  return new RateLimitError({
    retryAfterSeconds: positiveSeconds(response.headers?.get('Retry-After'))
      ?? positiveSeconds(body?.retryAfterSeconds),
    limiter: typeof body?.limiter === 'string' ? body.limiter : null,
    message: `API error 429: ${text}`,
  });
};

export const authFetch = async (path, options = {}) => {
  // Both custom options are stripped here so they never reach fetch().
  const { directBackend, redirectOnGate = true, ...fetchOptions } = options;
  try {
    // Wait until MSAL is ready
    await msalReady.catch(err => {
      console.error("MSAL initialization failed", err);
      return null;
    });

    if (directBackend && !config.api.directBaseUrl) {
      throw new Error('directBackend requested but REACT_APP_DIRECT_API_URL is not set for this build — no fallback backend origin exists');
    }
    const baseUrl = directBackend ? config.api.directBaseUrl : getApiBaseUrl();
    const normalizedPath = normalizePath(path);
    const url = `${baseUrl}${normalizedPath}`;

    // Ensure we have an active account
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (account) msalInstance.setActiveAccount(account);

    // Get access token
    const token = await getAuthToken();

    // Prepare headers
    const headers = fetchOptions.headers || {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (process.env.NODE_ENV !== 'production') {
      headers['x-dev-user'] = 'devUser';
    } else {
      console.warn('No authentication token available');
      throw new Error('Authentication required');
    }

    // Make the fetch request
    const response = await fetch(url, {
      ...fetchOptions,
      headers
    });

    // 402 = subscription gate. Not an auth problem, so no token-refresh
    // retry — send the user to the subscription page instead, and throw a
    // typed error so callers' catch blocks can stay quiet about it.
    // No navigation when the caller opted out (background calls) or when
    // the user is already on a gate-exempt page.
    if (response.status === 402) {
      if (redirectOnGate && !GATE_EXEMPT_PATH.test(window.location.pathname)) {
        window.location.assign('/abonnement');
      }
      const gateError = new Error('subscription_required');
      gateError.name = 'SubscriptionRequiredError';
      gateError.status = 402;
      throw gateError;
    }

    // 429 = rate limited. Typed error, no retry (see RateLimitError).
    if (response.status === 429) {
      throw await rateLimitErrorFrom(response);
    }

    // Handle 401/403 with a single forced refresh retry
    if ((response.status === 401 || response.status === 403) && account) {
      try {
        const refreshResponse = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account,
          forceRefresh: true,
        });

        if (refreshResponse?.accessToken) {
          headers['Authorization'] = `Bearer ${refreshResponse.accessToken}`;
          const retryResponse = await fetch(url, { ...fetchOptions, headers });
          if (retryResponse.ok) return retryResponse;
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
      console.warn('Authentication failed after token refresh attempt');
    }

    if (!response.ok) {
      // `status` rides along so callers can branch on specific codes
      // (e.g. 409 already-active / 502 Vipps-unreachable on /subscription).
      let apiError;
      try {
        const errorText = await response.text();
        apiError = new Error(`API error ${response.status}: ${errorText}`);
      } catch (textError) {
        apiError = new Error(`API error ${response.status}`);
      }
      apiError.status = response.status;
      throw apiError;
    }

    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

/**
 * Convenience methods for common HTTP operations
 */
export const api = {
  get: (path, options = {}) => authFetch(path, { ...options, method: 'GET' }),

  post: (path, data, options = {}) => authFetch(path, {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    body: JSON.stringify(data)
  }),

  put: (path, data, options = {}) => authFetch(path, {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    body: JSON.stringify(data)
  }),

  delete: (path, options = {}) => authFetch(path, { ...options, method: 'DELETE' })
};
